import { useState, useEffect } from 'react';
import { useParams, Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Play, Pause, SkipForward, Globe } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { Playlist, Story } from '@shared/schema';
import Navigation from '@/components/Navigation';

interface PlaylistWithStories extends Playlist {
  stories: Array<{
    id: number;
    playlistId: number;
    storyId: number;
    position: number;
    addedAt: Date;
    story: Story;
  }>;
}

export default function SharedPlaylistPage() {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);

  // Fetch public playlist by share slug
  const { data: playlist, isLoading, error } = useQuery<PlaylistWithStories>({
    queryKey: ['/api/playlists/shared', slug],
    queryFn: async () => {
      const response = await fetch(`/api/playlists/shared/${slug}`);
      if (!response.ok) {
        throw new Error('Playlist not found');
      }
      return response.json();
    },
    enabled: !!slug,
  });

  // Sorted stories for consistent ordering
  const sortedStories = playlist?.stories ? [...playlist.stories].sort((a, b) => a.position - b.position) : [];

  // Auto-play logic
  useEffect(() => {
    if (!isAutoPlaying || !sortedStories.length) return;
    
    const timer = setTimeout(() => {
      handleNext();
    }, 30000);

    return () => clearTimeout(timer);
  }, [isAutoPlaying, currentStoryIndex, sortedStories]);

  const handleNext = () => {
    if (!sortedStories.length) return;
    if (currentStoryIndex < sortedStories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else {
      setIsAutoPlaying(false);
      toast({
        title: 'Playlist complete',
        description: 'You\'ve reached the end of the playlist',
      });
    }
  };

  const handlePrevious = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-teal-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !playlist) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-teal-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Playlist Not Found</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                This playlist doesn't exist or is not publicly available
              </p>
              <Link href="/">
                <Button className="w-full" data-testid="button-back-home">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const currentStory = sortedStories[currentStoryIndex]?.story;

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-teal-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/">
            <Button variant="ghost" className="mb-4" data-testid="button-back">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent" data-testid="text-playlist-name">
                  {playlist.name}
                </h1>
                <Badge variant="default" data-testid="badge-visibility-public">
                  <Globe className="h-3 w-3 mr-1" /> Public Playlist
                </Badge>
              </div>
              {playlist.description && (
                <p className="text-gray-600 dark:text-gray-400" data-testid="text-playlist-description">
                  {playlist.description}
                </p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2" data-testid="text-story-count">
                {sortedStories.length} {sortedStories.length === 1 ? 'story' : 'stories'}
              </p>
            </div>
          </div>
        </div>

        {sortedStories.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400">
                This playlist is empty
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Story Player */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle data-testid="text-current-story-title">
                      {currentStory?.title || 'Untitled Story'}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevious}
                        disabled={currentStoryIndex === 0}
                        data-testid="button-previous-story"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                        data-testid="button-toggle-autoplay"
                      >
                        {isAutoPlaying ? (
                          <><Pause className="h-4 w-4 mr-1" /> Pause</>
                        ) : (
                          <><Play className="h-4 w-4 mr-1" /> Auto-Play</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNext}
                        disabled={currentStoryIndex === sortedStories.length - 1}
                        data-testid="button-next-story"
                      >
                        <SkipForward className="h-4 w-4 mr-1" />
                        Next
                      </Button>
                    </div>
                  </div>
                  {currentStory && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <span data-testid="text-story-index">
                        Story {currentStoryIndex + 1} of {sortedStories.length}
                      </span>
                      <span>•</span>
                      <span>Language: {currentStory.language?.toUpperCase() || 'EN'}</span>
                      {currentStory.childName && (
                        <>
                          <span>•</span>
                          <span>For: {currentStory.childName}</span>
                        </>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {currentStory ? (
                    <div className="space-y-4">
                      {/* Story Preview */}
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                        <p className="text-gray-700 dark:text-gray-300 line-clamp-6" data-testid="text-story-preview">
                          {currentStory.content?.substring(0, 300)}...
                        </p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          Sign in to read the full story
                        </p>
                        <Link href="/auth">
                          <Button data-testid="button-signin-to-read">
                            Sign In
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">No story selected</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Playlist Sidebar */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Stories in Playlist</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {sortedStories.map((playlistStory, index) => (
                      <button
                        key={playlistStory.id}
                        onClick={() => setCurrentStoryIndex(index)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          currentStoryIndex === index
                            ? 'bg-gradient-to-r from-purple-100 to-teal-100 dark:from-purple-900/30 dark:to-teal-900/30 border-2 border-purple-500'
                            : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        data-testid={`button-select-story-${index}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium" data-testid={`text-story-title-${index}`}>
                              {playlistStory.story?.title || 'Untitled'}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {playlistStory.story?.childName || 'Unknown'}
                            </div>
                          </div>
                          {currentStoryIndex === index && (
                            <div className="text-purple-600 dark:text-purple-400">
                              <Play className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
